/**
 * ExportButton component - Dropdown button for exporting progress data.
 *
 * Material Design themed with PDF and CSV export options.
 */

import React, { useState } from 'react';
import { useExportPDF, useExportCSV } from '../../services/api';
import {
  downloadFile,
  generateExportFilename,
  calculateDateRange,
  handleExportError
} from '../../utils/exportUtils';

interface ExportButtonProps {
  dateRange: number; // Number of days
  className?: string;
}

const ExportButton: React.FC<ExportButtonProps> = ({ dateRange, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportPDF = useExportPDF();
  const exportCSV = useExportCSV();

  const handleExportPDF = async () => {
    setError(null);
    setIsOpen(false);

    try {
      const { start_date, end_date } = calculateDateRange(dateRange);

      const blob = await exportPDF.mutateAsync({
        dateRange: { start_date, end_date },
        sections: {
          include_summary: true,
          include_hrv_trends: true,
          include_sleep_stage_trends: true,
          include_correlations: true,
          include_pattern_insights: true
        }
      });

      const filename = generateExportFilename('pdf', start_date, end_date);
      downloadFile(blob, filename);
    } catch (err: any) {
      const errorMsg = handleExportError(err);
      setError(errorMsg);
      console.error('PDF export error:', err);
    }
  };

  const handleExportCSV = async () => {
    setError(null);
    setIsOpen(false);

    try {
      const { start_date, end_date } = calculateDateRange(dateRange);

      const blob = await exportCSV.mutateAsync({
        start_date,
        end_date,
        include_calculated_metrics: true
      });

      const filename = generateExportFilename('csv', start_date, end_date);
      downloadFile(blob, filename);
    } catch (err: any) {
      const errorMsg = handleExportError(err);
      setError(errorMsg);
      console.error('CSV export error:', err);
    }
  };

  const isLoading = exportPDF.isPending || exportCSV.isPending;

  return (
    <div className={`relative ${className}`}>
      {/* Export Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className="px-5 py-2.5 bg-gradient-to-r from-accent-500 to-accent-600 text-white font-semibold rounded-xl hover:from-accent-600 hover:to-accent-700 transition-all duration-200 shadow-soft hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
      >
        <span>📄</span>
        <span>{isLoading ? 'Exporting...' : 'Export Report'}</span>
        {!isLoading && <span className="text-sm">▼</span>}
      </button>

      {/* Dropdown Menu */}
      {isOpen && !isLoading && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          ></div>

          {/* Menu */}
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-elegant border border-neutral-200 z-20 overflow-hidden">
            <div className="p-2">
              <button
                onClick={handleExportPDF}
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-primary-50 transition-colors duration-150 flex items-start space-x-3"
              >
                <span className="text-2xl">📄</span>
                <div className="flex-1">
                  <div className="font-semibold text-neutral-900">Export as PDF</div>
                  <div className="text-sm text-neutral-600 mt-1">
                    Comprehensive report with charts and insights
                  </div>
                </div>
              </button>

              <button
                onClick={handleExportCSV}
                className="w-full text-left px-4 py-3 rounded-lg hover:bg-primary-50 transition-colors duration-150 flex items-start space-x-3 mt-1"
              >
                <span className="text-2xl">📊</span>
                <div className="flex-1">
                  <div className="font-semibold text-neutral-900">Export as CSV</div>
                  <div className="text-sm text-neutral-600 mt-1">
                    Raw data for spreadsheet analysis
                  </div>
                </div>
              </button>
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-white border-t border-primary-200 px-4 py-3">
              <div className="text-xs text-neutral-600">
                <strong>Date Range:</strong> Last {dateRange} days
              </div>
            </div>
          </div>
        </>
      )}

      {/* Error Message */}
      {error && (
        <div className="absolute right-0 mt-2 w-80 bg-gradient-to-br from-red-50 to-white border border-red-200 rounded-xl shadow-elegant p-4 z-20">
          <div className="flex items-start space-x-3">
            <span className="text-2xl">⚠️</span>
            <div className="flex-1">
              <div className="font-semibold text-red-900 mb-1">Export Failed</div>
              <div className="text-sm text-red-800">{error}</div>
              <button
                onClick={() => setError(null)}
                className="mt-3 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors duration-150"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-30">
          <div className="bg-white rounded-xl shadow-elegant p-8 max-w-sm">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
              <div className="text-lg font-semibold text-neutral-900">
                Generating Export...
              </div>
              <div className="text-sm text-neutral-600 text-center">
                This may take a moment for large date ranges
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportButton;
